import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/endpoints';
import { ApiError } from '../api/client';
import type { CustomerRow, Station } from '../api/types';
import {
  formatMinutes,
  formatMoney,
  poolForStationType,
  POOL_LABEL,
  STATION_TYPE_LABEL,
} from '../lib/format';
import { Ps5RatePicker, usePs5Rates } from './Ps5RatePicker';
import { useToast } from './Toast';
import { Badge, Button, EmptyState, ErrorNote, Input, Modal, Spinner } from './ui';

// Seating someone is the busiest interaction in the cafe: search by phone,
// see whether they can afford this station's pool, start. When they cannot,
// the dialog hands staff straight to the right recharge instead of a dead end.
export function StartSessionDialog({
  station,
  onClose,
  onStarted,
}: {
  station: Station;
  onClose: () => void;
  onStarted: () => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const pool = poolForStationType(station.station_type);
  const isPs5 = station.station_type === 'ps5';

  // PS5: the group picks players × duration and pays that flat price on the
  // payer's tab. No wallet is involved, so anyone with an account can start.
  const ps5 = usePs5Rates(isPs5);
  const rate = ps5.rate;

  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    // Debounced so typing a 10-digit phone number is one request, not ten.
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const result = await api.customers.list(search.trim(), 20, 0, controller.signal);
        setRows(result.customers);
        setError(null);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [search]);

  const minutesFor = useMemo(
    () => (row: CustomerRow) =>
      pool === 'simulator' ? row.simulator_minutes : row.general_minutes,
    [pool],
  );

  const start = async (customer: CustomerRow) => {
    setStarting(customer.customer_id);
    try {
      const result = await api.sessions.start(
        station.station_id,
        customer.customer_id,
        rate?.ps5_pricing_id,
      );
      if (isPs5) {
        toast.success(
          `${customer.name}'s group started on ${station.name} — ${formatMinutes(result.minutes_remaining)}, ${formatMoney(rate?.price)} on their tab.`,
        );
      } else if (result.agent_online) {
        toast.success(`${customer.name} started on ${station.name}.`);
      } else {
        // Billing has begun even though the PC has not been told to unlock.
        toast.info(
          `${customer.name} is started on ${station.name}, but that PC is not reporting in — the desktop stays locked until its agent checks in.`,
        );
      }
      onStarted();
      onClose();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message);
      // 409 on start is almost always an empty pool; the recovery is a sale,
      // so offer it rather than making staff re-navigate.
      if (apiError.status === 409 && pool && !isPs5) {
        toast.error(apiError.message);
      }
    } finally {
      setStarting(null);
    }
  };

  return (
    <Modal
      title={isPs5 ? `Start a group on ${station.name}` : `Seat a customer at ${station.name}`}
      onClose={onClose}
      wide
    >
      <div className="space-y-4">
        {isPs5 ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-400">
              Pick the group size and how long, then the customer who is paying. The price
              goes on their tab — no wallet time is used.
            </p>
            <Ps5RatePicker choice={ps5} />
          </div>
        ) : (
          <p className="text-sm text-slate-400">
            {STATION_TYPE_LABEL[station.station_type]} · pays from{' '}
            <span className="text-slate-200">{pool ? POOL_LABEL[pool] : '—'}</span>{' '}
            minutes. A customer with only the other pool cannot sit here.
          </p>
        )}

        <Input
          label="Search by phone or name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="9876543210"
          inputMode="search"
          autoFocus
        />

        {error && <ErrorNote>{error}</ErrorNote>}

        {loading && rows.length === 0 ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <EmptyState title="No customer matches that.">
            {isPs5
              ? 'First-time visitor? Create the account for whoever is paying, then start.'
              : 'First-time visitor? Create the account, sell a pack, then seat them.'}
            <div className="mt-3">
              <Button variant="secondary" onClick={() => navigate('/customers?new=1')}>
                Create customer
              </Button>
            </div>
          </EmptyState>
        ) : (
          <ul className="divide-y divide-slate-800 rounded-lg border border-slate-800">
            {rows.map((row) => {
              const minutes = minutesFor(row);
              // A PS5 is paid on the tab, so any active account can start one.
              const affordable = isPs5 || minutes > 0;
              return (
                <li
                  key={row.customer_id}
                  className="flex flex-wrap items-center justify-between gap-3 px-3 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-100">{row.name}</p>
                    <p className="text-xs text-slate-500">{row.phone}</p>
                    {/* Both pools, side by side — the pool that pays here is
                        highlighted so staff never mis-read the balance. */}
                    <div className={`mt-1 flex gap-2 ${isPs5 ? 'hidden' : ''}`}>
                      <Badge tone={pool === 'general' ? 'sky' : 'slate'}>
                        PC {formatMinutes(row.general_minutes)}
                      </Badge>
                      <Badge tone={pool === 'simulator' ? 'sky' : 'slate'}>
                        Sim {formatMinutes(row.simulator_minutes)}
                      </Badge>
                    </div>
                  </div>

                  {affordable ? (
                    <Button
                      onClick={() => start(row)}
                      disabled={starting !== null || (isPs5 && !rate)}
                      title={isPs5 && !rate ? 'Pick players and how long first' : undefined}
                    >
                      {starting === row.customer_id ? 'Starting…' : 'Start'}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={() =>
                        navigate(`/customers/${row.customer_id}?sell=${pool ?? 'general'}`)
                      }
                    >
                      Recharge
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
